import { useEffect, useRef, useCallback, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import ResourceCategoryItem from './ResourceCategoryItem';
import { mainCategories } from '../../data/resourceCategories';

/**
 * ResourceCarousel - Displays the 5 main resource categories
 * Uses back-and-forth (oscillating) auto-scroll instead of infinite loop
 * @param {Function} onCategoryClick - Handler when a category is clicked
 */
const ResourceCarousel = ({ onCategoryClick }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    skipSnaps: false,
    dragFree: true,
    containScroll: 'trimSnaps',
  });

  const isMountedRef = useRef(true);
  const autoplayIntervalRef = useRef(null);
  const directionRef = useRef(1);
  const isPausedRef = useRef(false);
  
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const updateScrollState = useCallback(() => {
    if (!emblaApi || !isMountedRef.current) return;
    
    try {
      const canNext = emblaApi.canScrollNext();
      const canPrev = emblaApi.canScrollPrev();
      setCanScrollNext(canNext);
      setCanScrollPrev(canPrev);
      setIsScrollable(canNext || canPrev);
    } catch {
    }
  }, [emblaApi]);

  const startAutoplay = useCallback(() => {
    if (!emblaApi || !isMountedRef.current) return;
    
    if (autoplayIntervalRef.current) {
      clearInterval(autoplayIntervalRef.current);
    }
    
    const isDesktop = window.innerWidth >= 560;
    if (isDesktop) return;
    
    autoplayIntervalRef.current = setInterval(() => {
      if (!isMountedRef.current || !emblaApi || isPausedRef.current) return;
      
      try {
        const canNext = emblaApi.canScrollNext();
        const canPrev = emblaApi.canScrollPrev();
        
        if (!canNext && !canPrev) {
          clearInterval(autoplayIntervalRef.current);
          return;
        }
        
        if (directionRef.current === 1 && !canNext) {
          directionRef.current = -1;
        } else if (directionRef.current === -1 && !canPrev) {
          directionRef.current = 1;
        }
        
        if (directionRef.current === 1) {
          emblaApi.scrollNext();
        } else {
          emblaApi.scrollPrev();
        }
      } catch {
      }
    }, 3000);
  }, [emblaApi]);


  const handleMouseEnter = useCallback(() => {
    isPausedRef.current = true;
  }, []);

  const handleMouseLeave = useCallback(() => {
    isPausedRef.current = false;
  }, []);

  const handleFocusIn = useCallback(() => {
    isPausedRef.current = true;
  }, []);

  const handleFocusOut = useCallback(() => {
    isPausedRef.current = false;
  }, []);

  const checkScrollable = useCallback(() => {
    if (!isMountedRef.current || !emblaApi) return;
    
    try {
      if (typeof emblaApi.canScrollNext !== 'function') return;
      
      const canScroll = emblaApi.canScrollNext() || emblaApi.canScrollPrev();
      const isDesktop = window.innerWidth >= 560;
      
      if (!canScroll || isDesktop) {
        if (autoplayIntervalRef.current) {
          clearInterval(autoplayIntervalRef.current);
          autoplayIntervalRef.current = null;
        }
      } else {
        startAutoplay();
      }
      
      updateScrollState();
    } catch {
    }
  }, [emblaApi, startAutoplay, updateScrollState]);

  useEffect(() => {
    isMountedRef.current = true;
    
    if (!emblaApi) return;
    
    checkScrollable();
    updateScrollState();
    
    emblaApi.on('scroll', updateScrollState);
    emblaApi.on('select', updateScrollState);
    emblaApi.on('reInit', () => {
      checkScrollable();
      updateScrollState();
    });
    
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (!isMountedRef.current || !emblaApi) return;
        try {
          if (typeof emblaApi.reInit === 'function') {
            emblaApi.reInit();
          }
          checkScrollable();
          updateScrollState();
        } catch {
        }
      }, 100);
    };
    window.addEventListener('resize', handleResize);
    
    return () => {
      isMountedRef.current = false;
      clearTimeout(resizeTimeout);
      if (autoplayIntervalRef.current) {
        clearInterval(autoplayIntervalRef.current);
      }
      if (emblaApi) {
        try {
          emblaApi.off('scroll', updateScrollState);
          emblaApi.off('select', updateScrollState);
          emblaApi.off('reInit', checkScrollable);
        } catch {
          // Handle errors gracefully
        }
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [emblaApi, checkScrollable, updateScrollState]);

  return (
    <div 
      className="resource-carousel relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocusIn}
      onBlur={handleFocusOut}
    >
      {/* Embla Viewport - overflow hidden on mobile, visible when centered */}
      <div className="embla__viewport overflow-hidden centered-mode:overflow-visible" ref={emblaRef}>
        {/* Embla Container */}
        <div className="embla__container flex">
          {mainCategories.map((category, index) => (
            <div 
              key={category.key} 
              className={`embla__slide flex-shrink-0 flex-grow-0 min-w-0 ${index === 0 ? 'pl-0' : ''}`}
            >
              <ResourceCategoryItem
                category={category}
                onClick={onCategoryClick}
              />
            </div>
          ))}
        </div>
      </div>

      {isScrollable && canScrollNext && (
        <div 
          className="carousel-fade-right absolute right-0 top-0 bottom-0 w-6 sm:w-12 pointer-events-none z-10"
          aria-hidden="true"
        />
      )}
      
      {isScrollable && canScrollPrev && (
        <div 
          className="carousel-fade-left absolute left-0 top-0 bottom-0 w-6 sm:w-12 pointer-events-none z-10"
          aria-hidden="true"
        />
      )}

      {/* 
        Responsive styles for 5 categories
        
        KEY BREAKPOINTS:
        - 320-559px: Carousel scrolls, left-aligned
        - 560px+: All 5 fit, centered with gaps
      */}
      <style>{`
        
        .carousel-fade-right {
          background: linear-gradient(to left, 
            rgba(255, 255, 255, 0.95) 0%, 
            rgba(255, 255, 255, 0.8) 30%,
            rgba(255, 255, 255, 0) 100%
          );
        }
        .carousel-fade-left {
          background: linear-gradient(to right, 
            rgba(255, 255, 255, 0.95) 0%, 
            rgba(255, 255, 255, 0.8) 30%,
            rgba(255, 255, 255, 0) 100%
          );
        }
        
        /* ========================================
           BASE - 320px (Mobile)
           Item: 88px, ~3.2 visible, carousel scrolls
           ======================================== */
        .resource-carousel .embla__slide {
          flex: 0 0 88px;
          padding-left: 6px;
        }
        .resource-carousel .embla__slide:first-child {
          padding-left: 0;
        }
        .resource-carousel .resource-icon {
          width: 54px;
          height: 54px;
        }
        .resource-carousel .resource-icon-inner {
          font-size: 22px;
        }
        .resource-carousel .resource-label {
          font-size: 9px;
          word-wrap: break-word;
          overflow-wrap: break-word;
          hyphens: none;
          -webkit-hyphens: none;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        /* ========================================
           375px+ 
           Item: 98px, ~3.4 visible
           ======================================== */
        @media (min-width: 375px) {
          .resource-carousel .embla__slide {
            flex: 0 0 98px;
            padding-left: 8px;
          }
          .resource-carousel .resource-icon {
            width: 58px;
            height: 58px;
          }
          .resource-carousel .resource-icon-inner {
            font-size: 24px;
          }
          .resource-carousel .resource-label {
            font-size: 10px;
          }
        }
        
        /* ========================================
           425px+ 
           Item: 108px, ~3.6 visible
           ======================================== */
        @media (min-width: 425px) {
          .resource-carousel .embla__slide {
            flex: 0 0 108px;
            padding-left: 10px;
          }
          .resource-carousel .resource-icon {
            width: 62px;
            height: 62px;
          }
          .resource-carousel .resource-icon-inner {
            font-size: 26px;
          }
          .resource-carousel .resource-label {
            font-size: 10px;
          }
        }
        
        /* ========================================
           560px+ - CENTERED MODE BEGINS
           All 5 items fit, centered with even gaps
           Item: 100px, gap: 8px
           Total: 5×100 + 4×8 = 532px (fits in ~540px usable)
           ======================================== */
        @media (min-width: 560px) {
          .resource-carousel .embla__viewport {
            overflow: visible;
          }
          .resource-carousel .embla__container {
            justify-content: center;
            gap: 8px;
            transform: none !important;
          }
          .resource-carousel .embla__slide {
            flex: 0 0 auto;
            width: 100px;
            padding-left: 0;
            transform: none !important;
          }
          .resource-carousel .resource-icon {
            width: 60px;
            height: 60px;
          }
          .resource-carousel .resource-icon-inner {
            font-size: 26px;
          }
          .resource-carousel .resource-label {
            font-size: 10px;
          }
          
          .carousel-fade-right,
          .carousel-fade-left {
            display: none;
          }
        }
        
        /* ========================================
           640px+ (sm)
           Item: 110px, gap: 10px
           Total: 5×110 + 4×10 = 590px
           ======================================== */
        @media (min-width: 640px) {
          .resource-carousel .embla__container {
            gap: 10px;
          }
          .resource-carousel .embla__slide {
            width: 110px;
          }
          .resource-carousel .resource-icon {
            width: 64px;
            height: 64px;
          }
          .resource-carousel .resource-icon-inner {
            font-size: 28px;
          }
          .resource-carousel .resource-label {
            font-size: 10px;
          }
        }
        
        /* ========================================
           768px+ (md)
           Item: 130px, gap: 12px
           Total: 5×130 + 4×12 = 698px
           ======================================== */
        @media (min-width: 768px) {
          .resource-carousel .embla__container {
            gap: 12px;
          }
          .resource-carousel .embla__slide {
            width: 130px;
          }
          .resource-carousel .resource-icon {
            width: 70px;
            height: 70px;
          }
          .resource-carousel .resource-icon-inner {
            font-size: 30px;
          }
          .resource-carousel .resource-label {
            font-size: 11px;
          }
        }
        
        /* ========================================
           1024px+ (lg)
           Item: 145px, gap: 16px
           Total: 5×145 + 4×16 = 789px
           ======================================== */
        @media (min-width: 1024px) {
          .resource-carousel .embla__container {
            gap: 16px;
          }
          .resource-carousel .embla__slide {
            width: 145px;
          }
          .resource-carousel .resource-icon {
            width: 74px;
            height: 74px;
          }
          .resource-carousel .resource-icon-inner {
            font-size: 32px;
          }
          .resource-carousel .resource-label {
            font-size: 12px;
          }
        }
        
        /* ========================================
           1280px+ (xl)
           Item: 160px, gap: 20px
           Total: 5×160 + 4×20 = 880px
           ======================================== */
        @media (min-width: 1280px) {
          .resource-carousel .embla__container {
            gap: 20px;
          }
          .resource-carousel .embla__slide {
            width: 160px;
          }
          .resource-carousel .resource-icon {
            width: 78px;
            height: 78px;
          }
          .resource-carousel .resource-icon-inner {
            font-size: 34px;
          }
          .resource-carousel .resource-label {
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default ResourceCarousel;
